import { useQuery } from '@tanstack/react-query';
import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import * as React from 'react';
import {
  AvailabilityQueryKey,
  getData,
  getFunctionalParser,
  getOverwrittenRules,
  getPorts,
  getShippingLinesMapping,
  getGateHours,
  getAvailabilityAppointments,
} from 'services/clients/availability';
import { Terminal } from 'types/Shipping';
import { useRegion } from 'common/hooks/region';
import { TerminalQueryKeys } from 'services/clients/terminal';
import { formatData } from 'services/helpers/data';
import { merge } from 'lodash';
import { ERL_STOP_THE_CLOCK_PARAMS } from './params';
import { GET_EMPTY_APPOINTMENTS_INTERVAL } from './constants/appointments.constants';

export interface ErlContextType {
  terminals: Terminal[];
  functionalParsers: { port: string; last_update: string }[];
  shippingLineMapping: { port: string; shipping_line: string }[];
  gateHours: {
    closing: null | string;
    date: string;
    is_open: boolean;
    last_update: string;
    opening: null | string;
    port: string;
    shift: string;
    warnings: string[];
  }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  availabilities: Record<string, any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  appointments: Record<string, any>;
  loading: boolean;
  fetching: boolean;
}

const INITIAL_DATA: ErlContextType = {
  terminals: [],
  functionalParsers: [],
  shippingLineMapping: [],
  gateHours: [],
  availabilities: {},
  appointments: {},
  loading: true,
  fetching: false,
};

export const ErlContext = React.createContext<ErlContextType>(INITIAL_DATA);

export const useErlData = (): ErlContextType => {
  const region = useRegion();
  const { searchParams } = useCustomSearchParams(ERL_STOP_THE_CLOCK_PARAMS);

  const { forDatetime, shippingLine, containerType } = searchParams;

  const terminalsQuery = useQuery({
    queryKey: [TerminalQueryKeys.Terminals, region.code, forDatetime],
    queryFn: ({ signal }) => getPorts(region.code, forDatetime, signal),
    enabled: !!region.code,
  });

  const erlDataQuery = useQuery({
    queryKey: [
      AvailabilityQueryKey.TableData,
      shippingLine,
      containerType,
      region.code,
      forDatetime,
    ],
    queryFn: ({ signal }) =>
      getData([shippingLine], [containerType], region.code, forDatetime, signal),
    enabled: !!shippingLine && !!containerType && !!region.code,
  });

  const overwrittenRulesQuery = useQuery({
    queryKey: [
      AvailabilityQueryKey.OverwrittenRules,
      shippingLine,
      containerType,
      region.code,
      forDatetime,
    ],
    queryFn: ({ signal }) =>
      getOverwrittenRules([shippingLine], [containerType], region.code, forDatetime, signal),
    enabled: !!shippingLine && !!containerType && !!region.code,
  });

  const functionalParsersQuery = useQuery({
    queryKey: [AvailabilityQueryKey.FunctionalParser, region.code, forDatetime],
    queryFn: ({ signal }) => getFunctionalParser(region.code, forDatetime, signal),
    enabled: !!region.code,
  });

  const shippingLineMappingQuery = useQuery({
    queryKey: [AvailabilityQueryKey.ShippingLineMapping, region.code, forDatetime],
    queryFn: ({ signal }) => getShippingLinesMapping(region.code, forDatetime, signal),
    enabled: !!region.code,
  });

  const gateHoursQuery = useQuery({
    queryKey: [AvailabilityQueryKey.GateHours, region.code, forDatetime],
    queryFn: ({ signal }) => getGateHours([region.code], forDatetime, signal),
    enabled: !!region.code,
  });

  const availabilityAppointmentsQuery = useQuery({
    queryKey: [
      AvailabilityQueryKey.AvailabilityAppointments,
      shippingLine,
      containerType,
      region.code,
      region.timeZone,
      forDatetime,
    ],
    queryFn: ({ signal }) =>
      getAvailabilityAppointments(
        shippingLine,
        containerType,
        region.code,
        region.timeZone,
        forDatetime,
        signal
      ),
    enabled: !!shippingLine && !!containerType && !!region.code,
  });

  React.useEffect(() => {
    const performRefresh = () => {
      terminalsQuery.refetch();
      erlDataQuery.refetch();
      overwrittenRulesQuery.refetch();
      functionalParsersQuery.refetch();
      shippingLineMappingQuery.refetch();
      gateHoursQuery.refetch();
      availabilityAppointmentsQuery.refetch();
    };

    if (!forDatetime) {
      const refresh = window.setInterval(performRefresh, GET_EMPTY_APPOINTMENTS_INTERVAL);
      return () => window.clearInterval(refresh);
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forDatetime, shippingLine, containerType, region.code]);

  return React.useMemo((): ErlContextType => {
    const loading =
      terminalsQuery.isLoading ||
      erlDataQuery.isLoading ||
      overwrittenRulesQuery.isLoading ||
      functionalParsersQuery.isLoading ||
      shippingLineMappingQuery.isLoading ||
      gateHoursQuery.isLoading ||
      availabilityAppointmentsQuery.isLoading;

    const fetching =
      terminalsQuery.isFetching ||
      erlDataQuery.isFetching ||
      overwrittenRulesQuery.isFetching ||
      functionalParsersQuery.isFetching ||
      shippingLineMappingQuery.isFetching ||
      gateHoursQuery.isFetching ||
      availabilityAppointmentsQuery.isFetching;

    if (loading) {
      return INITIAL_DATA;
    }

    const formattedData = erlDataQuery.data ? formatData(erlDataQuery.data, region.timeZone) : {};
    const formattedRules = overwrittenRulesQuery.data
      ? formatData(overwrittenRulesQuery.data, region.timeZone)
      : {};

    const availabilities = merge(formattedData, formattedRules);

    return {
      terminals: terminalsQuery.data || [],
      functionalParsers: functionalParsersQuery.data || [],
      shippingLineMapping: shippingLineMappingQuery.data || [],
      gateHours: gateHoursQuery.data || [],
      appointments: availabilityAppointmentsQuery.data || [],
      availabilities,
      loading,
      fetching,
    };
  }, [
    region,
    terminalsQuery,
    erlDataQuery,
    overwrittenRulesQuery,
    functionalParsersQuery,
    shippingLineMappingQuery,
    gateHoursQuery,
    availabilityAppointmentsQuery,
  ]);
};
